import { useState, useCallback, useEffect } from "react";

export interface QueryHistoryItem {
  id: string;
  drug_name: string;
  dosage_form: string;
  task_id: string;
  status: "DONE" | "ERROR" | "RUNNING";
  created_at: string;
  summary?: string;
}

const STORAGE_KEY = "pharmaintel_query_history";
const MAX_HISTORY_ITEMS = 200;
const HISTORY_RETENTION_DAYS = 7;

function getStoredHistory(): QueryHistoryItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: QueryHistoryItem[] = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - HISTORY_RETENTION_DAYS * 86400000;
    return parsed.filter((item) => {
      const ts = new Date(item.created_at).getTime();
      return !isNaN(ts) && ts > cutoff;
    });
  } catch {
    return [];
  }
}

function saveHistory(items: QueryHistoryItem[]) {
  try {
    const trimmed = items.slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // storage full or unavailable — non-critical
  }
}

export function useQueryHistory() {
  const [history, setHistory] = useState<QueryHistoryItem[]>(() => getStoredHistory());

  useEffect(() => {
    const handler = () => setHistory(getStoredHistory());
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const addHistory = useCallback((item: Omit<QueryHistoryItem, "id" | "created_at">) => {
    const entry: QueryHistoryItem = {
      ...item,
      id: `${item.task_id}_${Date.now()}`,
      created_at: new Date().toISOString(),
    };
    setHistory((prev) => {
      const next = [entry, ...prev].slice(0, MAX_HISTORY_ITEMS);
      saveHistory(next);
      return next;
    });
  }, []);

  const removeHistory = useCallback((id: string) => {
    setHistory((prev) => {
      const next = prev.filter((item) => item.id !== id);
      saveHistory(next);
      return next;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  }, []);

  const getHistoryById = useCallback((taskId: string) => {
    return history.find((item) => item.task_id === taskId) ?? null;
  }, [history]);

  return { history, addHistory, removeHistory, clearHistory, getHistoryById };
}
